import { Component, OnInit, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { catchError, finalize } from "rxjs";
import { ToastrService } from "ngx-toastr";

// NG-ZORRO Imports (Standard Set)
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';

import { HttpUserService } from "../../../services/user/http-user.service";
import { PermissionEnum, UserDto, UserStatusEnum } from "../../../dto";
import { UserActionService } from "./user-action.service";
import { AddUserModalComponent } from "../add-user/add-user-modal.component";
import { EditUserModalComponent } from "../edit-user/edit-user-modal.component";
import { ResetPasswordModalComponent } from "../../../components/reset-password/reset-password-modal.component";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { AuthService } from "../../../services/auth/auth.service";
import { PermissionsModalComponent } from "../permissions/permissions-modal.component";

@Component({
    selector: 'app-view-user',
    standalone: true,
    imports: [
        CommonModule, 
        ReactiveFormsModule, 
        
        // Zorro Modules
        NzTableModule,
        NzButtonModule,
        NzIconModule,
        NzCardModule,
        NzDividerModule,
        NzTooltipModule,
        NzPopconfirmModule,
		NzDropDownModule,

        // Modals
        AddUserModalComponent, 
        ResetPasswordModalComponent, 
        EditUserModalComponent,
        PermissionsModalComponent,
    ],
    templateUrl: './view-user.component.html',
    styleUrls: ['./view-user.component.scss']
})
export class ViewUserComponent implements OnInit {

    users: UserDto[] = [];
    selectedUser!: UserDto;
    loading = false; // Standard loading flag

    PermissionEnum = PermissionEnum;

    @ViewChild(AddUserModalComponent) addUserModalComponent!: AddUserModalComponent;
    @ViewChild(ResetPasswordModalComponent) resetPasswordModal!: ResetPasswordModalComponent;
    @ViewChild(EditUserModalComponent) editUserModalComponent!: EditUserModalComponent;
    @ViewChild(PermissionsModalComponent) permissionsModal!: PermissionsModalComponent;

    constructor(
        private toastr: ToastrService, 
        private httpService: HttpUserService, 
        private userActionService: UserActionService,
        public authService: AuthService
    ) { }

    ngOnInit(): void {
        this.fetchAllUsers();
    }

    fetchAllUsers() {
        const search: UserDto = {};
        this.loading = true;

        this.httpService.getAllUsers(search)
            .pipe(
                catchError(error => {
                    console.error('Error fetching users', error);
                    this.toastr.error('Failed to fetch user: ' + (error.error?.error || error.message));
                    throw error;
                }),
                finalize(() => this.loading = false)
            )
            .subscribe(data => {
                this.users = data;
            });
    }

    async disable(user: UserDto) {
        const success = await this.userActionService.disableUser(user);
        if (success) this.fetchAllUsers();
    }

    async enable(user: UserDto) {
        this.enableUnblock(user, 'enable');
    }

    async unblock(user: UserDto) {
        this.enableUnblock(user, 'unblock');
    }

    private async enableUnblock(user: UserDto, type: 'enable' | 'unblock') {
        const success = await this.userActionService.enableOrUnblockUser(user, type);
        if (success) this.fetchAllUsers();
    }

    addUser() {
        this.addUserModalComponent.open();
    }

    resetPassword(user: UserDto) {
        this.selectedUser = user;
        this.resetPasswordModal.open(this.selectedUser);
    }

    editUser(user: UserDto) {
        this.selectedUser = user;
        this.editUserModalComponent.open(this.selectedUser);
    }

    editPermissions(user: UserDto) {
        this.selectedUser = user;
        this.permissionsModal.open(this.selectedUser);
    }

    // Helper properties for cleaner template logic
    UserStatusEnum = UserStatusEnum; // Make enum available in template

    onSave() {
        this.fetchAllUsers();
    }
}